import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import type { PriceMolProps } from "@onestore/hel/dist/components/molecules/PriceMol"
import _t from "@core/i18n"
import type { PeriodInfo } from "@core/period-info"
import type { PriceType } from "@core/types"
import Price, { type PriceProps } from "@ionos-web-design-system/react/price"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { PriceInfo } from "~/fragments/priceInfo"
import isEmpty from "~/lib/isEmpty"
import { getDetailedPriceData } from "./helpers/getDetailedPriceData"

export type DetailedPriceProps = {
  variant: PriceMolProps["variant"]
  layoutVariant?: PriceProps["variant"]
  periodInfo: PeriodInfo
  customRenewPriceTexts?: string[]
  priceInfo?: PriceInfo
  hasHiddenBadge?: boolean
  showPriceInfoLabelsOnlyOnDefaultPeriod?: boolean
  dataTestId?: PriceMolProps["mainPrice"]["dataTest"]
  hasHiddenOmnibusRegularPriceText?: boolean
  hasPromoTopLabel?: boolean
  hasInlineLowestPriceText?: boolean
  spaces?: PriceMolProps["spaces"]
  hasNoTopLine?: boolean
  hasFlatRenderMode?: boolean
  hasSeparateCurrency?: boolean
}

export const getSizeFromVariant = (
  variant: PriceMolProps["variant"]
): "small" | "medium" | "large" => {
  if ("type" in variant) {
    return variant.type as "small" | "medium" | "large"
  }
  return variant.large.type as "small" | "medium" | "large"
}

export const getAlignmentFromVariant = (
  variant: PriceMolProps["variant"]
): "left" | "centered" | "right" => {
  let align: string

  if ("align" in variant) {
    align = variant.align as string
  } else {
    align = variant.large.align as string
  }

  // Map "center" to "centered" for Price component compatibility
  if (align === "center") {
    return "centered"
  }

  return align as "left" | "centered" | "right"
}

export const getMonthlyPriceElement = (
  price: string,
  currentPriceType: PriceType,
  prefix?: string
) => {
  const prefixText = !isEmpty(prefix) ? prefix : _t("prices.monthlyPricePrefix")
  const suffixText = _t("prices.monthlyPricePeriod")

  return (
    <>
      {prefixText}{" "}
      <TextAtm hasFitLineHeight isBold>
        {`${price} ${_t(`prices.${currentPriceType}`)} ${suffixText}`}
      </TextAtm>
    </>
  )
}

const DetailedPrice = ({
  variant,
  layoutVariant,
  periodInfo,
  customRenewPriceTexts,
  priceInfo,
  dataTestId,
  showPriceInfoLabelsOnlyOnDefaultPeriod,
  hasNoTopLine,
  hasFlatRenderMode,
  hasSeparateCurrency,
}: DetailedPriceProps): ReactElement<DetailedPriceProps> => {
  const { currentPriceType } = usePriceTypeContext()

  const priceData = getDetailedPriceData(
    currentPriceType,
    periodInfo,
    priceInfo,
    customRenewPriceTexts,
    showPriceInfoLabelsOnlyOnDefaultPeriod,
    hasNoTopLine,
    undefined,
    dataTestId,
    hasSeparateCurrency
  )

  const hasPromotion = periodInfo.hasPromotion()

  return (
    <Price
      alignment={getAlignmentFromVariant(variant)}
      data={priceData}
      size={getSizeFromVariant(variant)}
      variant={layoutVariant || "simple"}
      emphasis={hasPromotion}
      className="leading-normal"
      hasNoFixedSizes
      hasFlatRenderMode={hasFlatRenderMode}
      forcePostlineWeight
    />
  )
}

export default DetailedPrice
